import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from './Toast';
import { IcoTow, IcoRental, IcoTag } from './Icons';

const PRESET_TAGS = [
  { type: 'tow', label: 'Tow Bill' },
  { type: 'rental', label: 'Rental Bill' },
];

export default function TagsTab({ claim, session, room, onRefresh, onLocal, tl }) {
  const [customTag, setCustomTag] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null); // shrink-out animation before delete
  const tags = claim.tags || [];

  // Optimistic-UI: mutate the parent's claim.tags immediately; the realtime
  // subscription in ClaimModal reconciles the true state afterwards.
  const mutate = onLocal || (() => onRefresh());

  function hasTag(type, label) {
    return tags.some(t => t.type === type && t.label === label);
  }

  function logTag(label) {
    supabase.from('activity_log').insert({
      room_id: room.id,
      claim_id: claim.id,
      user_id: session.user.id,
      user_name: session.user.user_metadata?.full_name || session.user.email,
      action: 'tag_added',
      detail: `added tag "${label}" to ${claim.car || 'claim'}`,
    }).then(() => {});
  }

  async function togglePreset(preset) {
    const existing = tags.find(t => t.type === preset.type && t.label === preset.label);
    if (existing) {
      // Optimistic: uncheck immediately, restore if the delete fails
      mutate(c => ({ ...c, tags: (c.tags || []).filter(t => t.id !== existing.id) }));
      const { error } = await supabase.from('tags').delete().eq('id', existing.id);
      if (error) {
        mutate(c => ({ ...c, tags: [...(c.tags || []), existing] }));
        toast('Could not remove the tag — check your connection and try again.', 'error');
      }
    } else {
      // Optimistic: check immediately with a temp row, swap in the real row after
      const temp = { id: `temp-${Date.now()}`, claim_id: claim.id, type: preset.type, label: preset.label };
      mutate(c => ({ ...c, tags: [...(c.tags || []), temp] }));
      const { data, error } = await supabase.from('tags').insert({ claim_id: claim.id, type: preset.type, label: preset.label }).select().single();
      if (error) {
        mutate(c => ({ ...c, tags: (c.tags || []).filter(t => t.id !== temp.id) }));
        toast('Could not add the tag — check your connection and try again.', 'error');
        return;
      }
      mutate(c => ({ ...c, tags: (c.tags || []).map(t => (t.id === temp.id ? data : t)) }));
      logTag(preset.label);
    }
  }

  async function addCustomTag(e) {
    e.preventDefault();
    const label = customTag.trim();
    if (!label) return;
    // Optimistic: show the tag and clear the input immediately
    const temp = { id: `temp-${Date.now()}`, claim_id: claim.id, type: 'custom', label };
    mutate(c => ({ ...c, tags: [...(c.tags || []), temp] }));
    setCustomTag('');
    const { data, error } = await supabase.from('tags').insert({ claim_id: claim.id, type: 'custom', label }).select().single();
    if (error) {
      mutate(c => ({ ...c, tags: (c.tags || []).filter(t => t.id !== temp.id) }));
      setCustomTag(label);
      toast('Could not add the tag — check your connection and try again.', 'error');
      return;
    }
    mutate(c => ({ ...c, tags: (c.tags || []).map(t => (t.id === temp.id ? data : t)) }));
    logTag(label);
  }

  async function removeTag(tagId) {
    // Shrink the chip away, then remove it
    setRemovingId(tagId);
    await new Promise(r => setTimeout(r, 180));
    setRemovingId(null);
    const removed = tags.find(t => t.id === tagId);
    // Optimistic: remove immediately, restore if the delete fails
    mutate(c => ({ ...c, tags: (c.tags || []).filter(t => t.id !== tagId) }));
    const { error } = await supabase.from('tags').delete().eq('id', tagId);
    if (error && removed) {
      mutate(c => ({ ...c, tags: [...(c.tags || []), removed] }));
      toast('Could not remove the tag — check your connection and try again.', 'error');
    }
  }

  const customTags = tags.filter(t => t.type === 'custom');

  return (
    <div style={{ padding: 16 }}>
      {/* Preset tags */}
      <div style={{ marginBottom: 24 }}>
        <div className="label" style={{ marginBottom: 12 }}>Preset Tags</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PRESET_TAGS.map(preset => {
            const active = hasTag(preset.type, preset.label);
            return (
              <button
                key={preset.type}
                onClick={() => togglePreset(preset)}
                className="raise-3d"
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 16px',
                  background: active ? (preset.type === 'tow' ? 'rgba(234,179,8,0.1)' : 'rgba(59,130,246,0.1)') : tl ? 'var(--sp-bg)' : 'var(--bg2)',
                  border: '1px solid',
                  borderColor: active ? (preset.type === 'tow' ? 'rgba(234,179,8,0.4)' : 'rgba(59,130,246,0.4)') : tl ? 'var(--sp-border)' : 'transparent',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0, color: active ? (preset.type === 'tow' ? 'var(--yellow)' : 'var(--blue)') : 'var(--text2)' }}>{preset.type === 'tow' ? <IcoTow size={18} /> : <IcoRental size={18} />}</span>
                <span style={{
                  flex: 1,
                  fontSize: 14, fontWeight: 600,
                  color: active ? (preset.type === 'tow' ? 'var(--yellow)' : 'var(--blue)') : 'var(--text)',
                }}>
                  {preset.label}
                </span>
                <span style={{
                  width: 22, height: 22,
                  borderRadius: 5,
                  border: `2px solid ${active ? (preset.type === 'tow' ? 'var(--yellow)' : 'var(--blue)') : 'var(--border)'}`,
                  background: active ? (preset.type === 'tow' ? 'var(--yellow)' : 'var(--blue)') : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, color: 'white', fontWeight: 700, flexShrink: 0,
                }}>
                  {active ? '✓' : ''}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom tags */}
      <div>
        <div className="label" style={{ marginBottom: 12 }}>Custom Tags</div>
        <form onSubmit={addCustomTag} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input
            value={customTag}
            onChange={e => setCustomTag(e.target.value)}
            placeholder="e.g. Rush, Call Adjuster..."
            style={{ flex: 1, background: tl ? 'var(--sp-bg2)' : undefined, borderColor: tl ? 'var(--sp-border)' : undefined }}
          />
          <button className="btn btn-primary" type="submit" disabled={adding} style={{ flexShrink: 0, padding: '10px 14px' }}>
            {adding ? <div className="spinner" style={{ width: 16, height: 16 }} /> : 'Add'}
          </button>
        </form>

        {customTags.length === 0 ? (
          <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
            No custom tags yet
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {customTags.map(tag => (
              <div key={tag.id} className={removingId === tag.id ? 'removing' : undefined} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 10px',
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: 20,
                fontSize: 13, fontWeight: 500,
              }}>
                <span style={{ display: 'flex', alignItems: 'center', color: 'var(--text3)' }}><IcoTag size={13} /></span>{tag.label}
                <button onClick={() => removeTag(tag.id)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 15, padding: 0, lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
